import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { HiMagnifyingGlass } from "react-icons/hi2";

import { useCompanyNews } from "@/hooks/api/company/useCompanyNews";
import Badge from "@/components/atoms/Badge";
import FeaturedText from "@/components/molecules/FeaturedText";
import InfiniteScrollListSearchable from "@/components/organisms/InfiniteScrollListSearchable";

type CompanyDetailNewsProps = {
  id: string;
};

const CompanyDetailNews = ({ id }: CompanyDetailNewsProps) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState<string>("");
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useCompanyNews({
    id,
    enabled: !!id,
    query,
  });

  const flattenedData = useMemo(() => data?.pages?.flatMap((page) => page.data) ?? [], [data?.pages]);

  return flattenedData.length === 0 && query === "" && !isLoading ? (
    <div className={"py-10"}>
      <FeaturedText
        heading={t("search.noResults.title")}
        body={t("company.detail.overview.insights.news.noData")}
        size={"lg"}
        featuredIcon={{
          icon: HiMagnifyingGlass,
          color: "primary",
          size: "xl",
        }}
      />
    </div>
  ) : (
    <InfiniteScrollListSearchable
      title={t("company.detail.overview.insights.news.title")}
      onSearch={setQuery}
      items={flattenedData.filter((news) => !!news.link)}
      loadMore={fetchNextPage}
      hasMore={hasNextPage}
      isLoading={isLoading}
      isLoadingMore={isFetchingNextPage}
      containerClassName={"grid gap-6"}
      outerClassName={"py-7"}
      noQueryResultsNode={
        <div className={"py-10"}>
          <FeaturedText
            heading={t("search.noResults.title")}
            body={
              <Trans
                i18nKey={"company.detail.overview.insights.news.noDataQuery"}
                values={{ query }}
                components={{ bold: <strong /> }}
              />
            }
            size={"lg"}
            featuredIcon={{
              icon: HiMagnifyingGlass,
              color: "primary",
              size: "xl",
            }}
          />
        </div>
      }
      renderItem={(news) => (
        <div key={news.id}>
          <h4 className={"line-clamp-1 text-sm font-medium text-primary"}>{news.title}</h4>
          {news.categories.length > 0 && (
            <div className={"mt-2 flex gap-2"}>
              {news.categories.map((category) => (
                <Badge
                  label={t(
                    `company.detail.overview.insights.news.categories.${category}`,
                    t("company.detail.overview.insights.news.categories.other"),
                  )}
                  color={"natural"}
                  size={"sm"}
                />
              ))}
            </div>
          )}
          {news.link && (
            <>
              <p className={"mt-2 text-xs font-medium text-quinary"}>
                {t("company.detail.overview.insights.news.readMore")}
              </p>
              <a
                href={news.link}
                target="_blank"
                rel="noopener noreferrer"
                className={"mt-1 block text-sm text-link hover:underline"}
              >
                {news.link}
              </a>
            </>
          )}
          {news.date && <p className={"mt-1 text-xs text-quinary"}>{new Date(news.date).toLocaleDateString()}</p>}
        </div>
      )}
      renderLoading={Array.from({ length: 5 }).map((_, index) => (
        <div key={index}>
          <div className={"mb-2 h-5 w-4/5 animate-pulse rounded-full bg-gray-300"} />
          <div className={"mb-3 h-5 w-20 animate-pulse rounded-md bg-gray-300"} />
          <div className={"mb-2 h-5 w-28 animate-pulse rounded-full bg-gray-300"} />
          <div className={"mb-2 h-5 w-40 animate-pulse rounded-full bg-gray-300"} />
          <div className={"h-5 w-24 animate-pulse rounded-full bg-gray-300"} />
        </div>
      ))}
    />
  );
};

export default CompanyDetailNews;
